/**
 * 命令面板（⌘K）：打开一本书、页面跳转 + 能力目录。
 * 选中能力只把示例语句填进导师输入框，不自动执行；写入类操作始终经过对话或界面上的明确按钮。
 */
import { useNavigate } from "@tanstack/react-router";
import clsx from "clsx";
import { CornerDownLeft } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useCapabilities, useSources } from "@/shared/api/hooks";
import { useUi } from "@/shared/scene";

type Entry = { key: string; group: string; title: string; hint: string; run: () => void };

const PAGES: { to: string; title: string; hint: string }[] = [
  { to: "/", title: "书库", hint: "导入、收件箱、接着读" },
  { to: "/settings", title: "设置", hint: "模型、订阅登录、设备、外观" },
];

export function CommandPalette() {
  const open = useUi((s) => s.paletteOpen);
  const setOpen = useUi((s) => s.setPaletteOpen);
  const fillDraft = useUi((s) => s.fillDraft);
  const navigate = useNavigate();
  const { data } = useCapabilities();
  const books = useSources("active");
  const dialog = useRef<HTMLDialogElement>(null);
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);

  useEffect(() => {
    const el = dialog.current;
    if (!el) return;
    if (open && !el.open) {
      setQuery("");
      setActive(0);
      el.showModal();
    } else if (!open && el.open) el.close();
  }, [open]);

  const entries = useMemo<Entry[]>(() => {
    const close = () => setOpen(false);
    const pages = PAGES.map((p) => ({
      key: `page:${p.to}`,
      group: "页面",
      title: p.title,
      hint: p.hint,
      run: () => {
        close();
        void navigate({ to: p.to });
      },
    }));
    const opens = (books.data ?? []).map((b) => ({
      key: `book:${b.id}`,
      group: "打开",
      title: `《${b.title}》`,
      hint: b.progress ? `读到 ${Math.round(b.progress * 100)}%` : "",
      run: () => {
        close();
        void navigate({ to: "/read/$sourceId", params: { sourceId: b.id } });
      },
    }));
    const actions = (data?.actions ?? [])
      .filter((a) => a.available && a.examples.length && a.agent !== "none")
      .map((a) => ({
        key: `action:${a.id}`,
        group: "让导师做",
        title: a.title,
        hint: `“${a.examples[0] ?? ""}”`,
        run: () => {
          close();
          fillDraft(a.examples[0] ?? "");
        },
      }));
    const q = query.trim();
    const ask: Entry[] = q
      ? [
          {
            key: "ask",
            group: "问导师",
            title: `问导师：${q}`,
            hint: "填入导师输入框",
            run: () => {
              close();
              fillDraft(q);
            },
          },
        ]
      : [];
    const all = [...opens, ...pages, ...actions];
    const matched = q
      ? all.filter((e) => `${e.title}${e.hint}`.toLowerCase().includes(q.toLowerCase()))
      : all;
    return [...matched, ...ask];
  }, [data, books.data, query, navigate, fillDraft, setOpen]);

  const pick = entries[Math.min(active, entries.length - 1)];

  return (
    <dialog
      ref={dialog}
      aria-label="命令面板"
      onClose={() => setOpen(false)}
      onClick={(e) => e.target === dialog.current && setOpen(false)}
      onKeyDown={(e) => {
        if (e.key === "ArrowDown") {
          e.preventDefault();
          setActive((i) => Math.min(i + 1, entries.length - 1));
        } else if (e.key === "ArrowUp") {
          e.preventDefault();
          setActive((i) => Math.max(i - 1, 0));
        } else if (e.key === "Enter" && !e.nativeEvent.isComposing && pick) {
          e.preventDefault();
          pick.run();
        }
      }}
      className="mx-auto mt-[12vh] w-[min(560px,calc(100vw-32px))] rounded-l border border-border bg-surface p-0 text-text shadow-soft backdrop:bg-text/25"
    >
      <input
        aria-label="搜索页面与能力"
        autoFocus
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setActive(0);
        }}
        placeholder="去哪里、做什么，或者直接问导师…"
        className="h-12 w-full border-b border-border bg-transparent px-4 text-[14px] outline-none placeholder:text-text-3"
      />
      <ul className="max-h-[50vh] overflow-y-auto p-1.5">
        {entries.map((e, i) => (
          <li key={e.key}>
            {entries[i - 1]?.group !== e.group ? (
              <div className="px-2.5 pt-2 pb-1 text-[11.5px] text-text-3">{e.group}</div>
            ) : null}
            <button
              type="button"
              onMouseEnter={() => setActive(i)}
              onClick={e.run}
              className={clsx(
                "flex w-full items-center gap-3 rounded-s px-2.5 py-2 text-left text-[13.5px]",
                i === active && "bg-surface-2",
              )}
            >
              <span className="font-medium">{e.title}</span>
              <span className="min-w-0 flex-1 truncate text-[12.5px] text-text-3">{e.hint}</span>
              {i === active ? <CornerDownLeft className="size-3.5 text-text-3" /> : null}
            </button>
          </li>
        ))}
      </ul>
    </dialog>
  );
}
